import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useServiceSelection } from '../contexts/ServiceSelectionContext';
import { ShoppingCart, X, Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const FloatingBookingCart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedServices, removeService, getServiceCount, getTotalDuration } = useServiceSelection();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Show cart when services are selected, hide on booking page
  useEffect(() => {
    setIsVisible(getServiceCount() > 0 && location.pathname !== '/booking');
  }, [getServiceCount, location]);

  // Animate in when becoming visible
  useEffect(() => {
    if (getServiceCount() > 0) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [getServiceCount]);

  if (!isVisible) return null;

  const handleBookNow = () => {
    navigate('/booking');
    setIsExpanded(false);
  };

  return (
    <>
      {/* Backdrop when expanded */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 animate-fade-in"
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Floating Cart */}
      <div className={`
        fixed bottom-6 right-6 z-50 transition-all duration-300 transform
        ${isExpanded ? 'w-96' : 'w-auto'}
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
      `}>
        {!isExpanded ? (
          /* Collapsed State */
          <button
            onClick={() => setIsExpanded(true)}
            className="
              bg-gradient-to-r from-blue-600 to-cyan-600 text-white
              rounded-full shadow-2xl hover:shadow-3xl
              px-6 py-4 flex items-center gap-3
              hover:scale-105 transition-all duration-300
              group relative overflow-hidden
            "
          >
            {/* Animated background */}
            <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            
            <ShoppingCart className="w-6 h-6 animate-bounce" />
            <span className="font-semibold text-lg">
              {getServiceCount()} {getServiceCount() === 1 ? 'Service' : 'Services'}
            </span>
            <Badge className="bg-white/20 text-white border-0">
              {getTotalDuration()} min
            </Badge>
          </button>
        ) : (
          /* Expanded State */
          <div className="bg-white rounded-2xl shadow-2xl p-6 animate-scale-in">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
                Selected Services
              </h3>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Services List */}
            <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
              {selectedServices.map((service) => (
                <div
                  key={service.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{service.name}</p>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {service.duration}
                    </p>
                  </div>
                  <button
                    onClick={() => removeService(service.id)}
                    className="text-red-500 hover:text-red-700 transition-colors p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="border-t pt-4 mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Total Services:</span>
                <span className="font-medium">{getServiceCount()}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Estimated Duration:</span>
                <span className="font-medium">{getTotalDuration()} minutes</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button
                onClick={handleBookNow}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300 group"
              >
                <span>Book Now</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsExpanded(false)}
                className="w-full"
              >
                Continue Browsing
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingBookingCart;